import $ from 'jquery';
import _ from 'underscore';

import { paginationPlugin } from '@bingads-webui/projection-grid-pagination';
import { columnChooserPlugin } from '@bingads-webui-campaign/projection-grid-column-chooser';
import { editablePlugin } from '@bingads-webui-campaign/projection-grid-editable';
import { auctionInsightPlugin } from '@bingads-webui-campaign/projection-grid-auction-insight';
import { inlineDownloadWithoutFilterstatePlugin } from '@bingads-webui-campaign/projection-grid-inline-download';
import { jsdataRawDataPlugin as commonJsdataRawDataPlugin } from '@bingads-webui/projection-grid-js-data-raw-data';
import { periodOverPeriodPlugin } from '@bingads-webui-campaign/projection-grid-period-over-period';
import { segmentPlugin } from '@bingads-webui-campaign/projection-grid-segment';
import {
  campaignSegmentationTypeEnum,
  segmentationType,
} from '@bingads-webui-campaign/segmentation-utilities';
import { inlineErrorPlugin } from '@bingads-webui/projection-grid-inline-error';
import { bidStrategy } from '@bingads-webui-campaign/projection-grid-bid-strategy';
import { uiBlockerPlugin } from '@bingads-webui/projection-grid-uiblocker';
import { assistedSelectionPlugin } from '@bingads-webui/projection-grid-assisted-selection';
import { performanceLogPlugin, perfMarkerPlugin } from '@bingads-webui-campaign/pgrid-performance-log';
import { a11yPlugin } from '@bingads-webui/projection-grid-a11y';
import { aggregatedRowsPluginBuilder } from '@bingads-webui-campaign/pgrid-aggregated-rows';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { dateFilterPlugin } from '@bingads-webui-campaign/projection-grid-date-filter';
import { AdGroup } from '@bingads-webui-campaign/campaign-ui-schemas';
import { getPGridColumnResizePlugin } from '@bingads-webui-campaign/projection-grid-column-resize-helper';
import { projectionGridFlexColumnResize } from '@bingads-webui-campaign/projection-grid-flex-column-resize';
import { getGridName } from '@bingads-webui-campaign/inline-download-grids-metadata';

import { getColumnChooserConfig } from './columns/index';
import { mockEdm } from '../demo/mock/edm';

const appConfig = globalContext.require('component/config/index');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/ad-groups-tab');
const appState = globalContext.require('component/state/index');
const sessionStore = globalContext.requireDefault('component/js-data-storage/session-storage');
const edm = globalContext.requireDefault('component/edm-campaign/index');
const s$i18n = globalContext.require('$/schema-i18n!campaign');
const { DownloadView } = globalContext.require('campaignui/feature/griddownload/view/adapter');
const { setSegType, getSegType } = globalContext.require('component/bingads-grid/service/segmentation-preferences');
const ChangeBiddingStrategyModel = globalContext.require('module/model/bulkEdit/shared/changeBiddingStrategy');
const ChangeBiddingStrategyView = globalContext.require('module/view/bulkEdit/shared/changeBiddingStrategy');
const logger = globalContext.require('module/service/instrumentation/index');

const permissions = appConfig.get('Permissions');
const enableGridFlexLayout = _.result(permissions.dynamic, 'GridFlexLayout', false);
const jsdataRawDataPlugin = commonJsdataRawDataPlugin({
  getRawData: _.property('raw'),
  rawDataAccessor(ds) {
    // eslint-disable-next-line no-underscore-dangle
    return ds._rawData || ds.dataSource.dfmRawData;
  },
});

export function getAdGroupPlugins({
  filterService,
  router,
  scope,
  isForVNext,
  addDateChangeListener,
  p$daterange,
  adGroupTabPerfMarker,
  currentCampaign,
  preexecuteFlag = false,
  globalCampaignTypeFilter,
  getCampaignTypeGlobalFilter,
  preferenceService,
}) {
  const isSmartShoppingCampaign = currentCampaign &&
    (currentCampaign.CampaignSubType === 12 || currentCampaign.CampaignSubType === 'ShoppingSmartAds');
  const aggregatedRowsPlugin = aggregatedRowsPluginBuilder({
    i18n,
    keys: {
      overallI18nKey: 'AdGroupSummary_Total_New',
      filteredI18nKey: 'AdGroupSummary_Filtered_Total_New',
      waterfallFilterI18nKey: 'AdGroupSummary_Quickfilter_Total_New',
    },
    permissions,
    filterService,
    customConfig: {
      showLinkedInRow: true,
    },
  });
  const ReportEmailRule = appState.get('$ReportEmailRule');

  const columnChooserConfig = getColumnChooserConfig({
    scope,
    isForVNext,
    filterService,
    permissions,
    currentCampaign,
    globalCampaignTypeFilter,
  });

  if (isForVNext) {
    columnChooserConfig.customToolbarIcon = 'ba-TripleColumnSolid';
  }

  appState.unset('$ReportEmailRule');

  const getSegmentationTypes = () => {
    if (permissions.IsVerticalAdsReportingPhase1Enabled) {
      return [...campaignSegmentationTypeEnum, segmentationType.AdScenario];
    }

    return campaignSegmentationTypeEnum;
  };

  return _.compact([{
    // For accurate grid init latency, PerfMarkerPlugin should be the first plugin to load.
    plugin: perfMarkerPlugin,
    config: { appConfig, marker: adGroupTabPerfMarker },
  }, {
    plugin: paginationPlugin,
    config: {
      availablePageSizes: [20, 50, 100, 200],
      enableRandomPage: false,
      pageNumberLabelText: isForVNext ? '' : i18n.getString('Grid_Page_Number_Label_Text'),
      pageSizeText: i18n.getString('Grid_ShowRows'),
      totalPageCountText: i18n.getString('Grid_PageOf'),
      resetPageNumberOnSort: true,
    },
  },
  {
    plugin: auctionInsightPlugin,
    config: {
      isForVNext,
      pageName: 'adgroups',
      i18n,
      state: appState,
      legacyRouter: router,
      route: scope.campaignId ? 'campaign_adgroup_auction_insight' : 'adgroup_auction_insight',
      crossPageSelectNotAllowedI18nKey: 'AdGroupSummary_MultiplePageSelectionNotAllowed',
      shouldNavigateToCompetitionTab: permissions.IsBAACompetitionEnabled,
      scope,
      sessionStore,
      preferenceService,
      permissions,
      getCampaignTypeGlobalFilter,
    },
  },
  {
    plugin: inlineDownloadWithoutFilterstatePlugin,
    config: {
      isForVNext,
      p$daterange,

      DownloadView,
      isAvailable: true,
      gridId: 'AdGroupGrid',
      schema: AdGroup,
      s$i18n,
      i18n,
      ReportEmailRule,
    },
  },
  {
    plugin: segmentPlugin,
    config: {
      i18n,
      permissions,
      state: appState,
      isOdataActionSource: true,
      edm: preexecuteFlag ? mockEdm : edm,
      setSegType,
      getSegType,
      cultureShortDatePattern: appConfig.get('CultureDateTimeFormat').ShortDatePattern,
      cellTemplates: {
        AdGroup: 'default',
      },
      segmentationTypes: getSegmentationTypes(),
    },
  },
  !isSmartShoppingCampaign ? {
    plugin: bidStrategy,
    config: {
      isForVNext,
      ChangeBiddingStrategyView,
      ChangeBiddingStrategyModel,
      i18n,
      permissions,
    },
  } : null, !appConfig.get('CurrentAccountInActive') && !permissions.IsReadOnlyUser && {
    plugin: editablePlugin,
    config: { i18n },
  },
  {
    plugin: uiBlockerPlugin,
    config: { el: $(isForVNext ? '.ad-groups-page' : '#child-page') },
  },
  {
    plugin: performanceLogPlugin,
    config: { appConfig, logger },
  },
  {
    plugin: periodOverPeriodPlugin,
    config: { i18n },
  },
  {
    plugin: columnChooserPlugin,
    config: columnChooserConfig,
  },
  {
    plugin: dateFilterPlugin,
    config: {
      state: appState,
      isOdataActionSource: true,
      edm: preexecuteFlag ? mockEdm : edm,
      addDateChangeListener,
      p$daterange,
    },
  },
  {
    plugin: inlineErrorPlugin({
      errorIconClasses: ['grid-error'],
    }),
  },
  {
    plugin: assistedSelectionPlugin,
  },
  {
    plugin: a11yPlugin,
    config: { i18n },
  },
  {
    plugin: aggregatedRowsPlugin,
  },
  {
    plugin: jsdataRawDataPlugin,
  },
  ...enableGridFlexLayout ? [{
    plugin: projectionGridFlexColumnResize,
    config: {
      gridName: getGridName('AdGroup'),
      softDependencies: ['popColumns'],
      preferenceService,
    },
  }] : [getPGridColumnResizePlugin({
    gridName: getGridName('AdGroup'),
    preferenceService,
    permissions,
  })],
  ]);
}
